import { Edit, Add } from '@mui/icons-material';
import { FormControlLabel, Grid, Switch, useMediaQuery, Button, Divider, Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

import { Link, useHistory } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  Button as SMButton,
  Page,
  PageTitleWithButton,
  Pagination,
  SearchSection,
  Table
} from '../../../components';
import { MobileTable } from '../../../components/table/MobileTable';
import { getPlans } from '../../../fetch';
import { IPlan } from '../../../models';
import { PlansFilters } from './plan-filters';

export const ManagePlans = () => {
  const classes = useStyles();
  const history = useHistory();

  const [plans, setPlans] = useState<IPlan[]>([]);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [showArchived, setShowArchived] = useState(false);
  const [sortDirection, setSortDirection] = useState<{
    name?: 'Asc' | 'Desc';
    number?: 'Asc' | 'Desc';
    type?: 'Asc' | 'Desc';
  }>({
    name: 'Asc'
  });
  const [selectedSort, setSelectedSort] = useState<string>('Name');

  const [filters, setFilters] = useState<{
    name?: string;
  }>({});
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const fetchPlans = async () => {
    try {
      setIsLoading(true);
      const res = await getPlans({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort],
        name: searchedValue,
        showArchived,
        ...filters
      });
      setRecordCount(res.totalRecordCount);
      setPlans(res.records);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setPage(0);
  }, [showArchived]);

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, showArchived, searchedValue, selectedSort, sortDirection, filters]);

  const columns = [
    {
      Header: 'Plan Name',
      accessor: 'name',
      isServerSorted: selectedSort === 'name',
      isServerSortedDesc: sortDirection.name === 'Desc',
      handleClickColumn: () => handleClickColumn('name')
    },
    {
      Header: 'Plan Type',
      accessor: 'type',
      isServerSorted: selectedSort === 'type',
      isServerSortedDesc: sortDirection.type === 'Desc',
      handleClickColumn: () => handleClickColumn('type')
    },
    {
      Header: 'Plan Number',
      accessor: 'planNumber',
      isServerSorted: selectedSort === 'number',
      isServerSortedDesc: sortDirection.number === 'Desc',
      handleClickColumn: () => handleClickColumn('number')
    },
    {
      id: 'actions',
      accessor: '',
      Header: '',
      sort: false,

      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IPlan } };
      }) => {
        return (
          <div className={classes.actionButtons}>
            <SMButton
              id="edit-framer"
              type="button"
              color="primary"
              startIcon={<Edit />}
              onClick={() => handleEditPlan(original.planId)}
            >
              Edit
            </SMButton>
          </div>
        );
      }
    }
  ];

  const handleEditPlan = async (planId: number) => {
    history.push(`/development/plans/manage-plans/${planId}/general-information`);
  };

  return (
    <>
      <Page title="Manage Plans">
        <PageTitleWithButton
          pageTitleProps={{
            title: 'Manage Plans'
          }}
          button={
            <Box display="flex">
              <FormControlLabel
                control={
                  <Switch
                    className="print--none"
                    color="primary"
                    checked={showArchived}
                    id="active-inactive"
                    disabled={isLoading}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setShowArchived(e.target.checked);
                    }}
                  />
                }
                label="Show Inactive"
              />
              <Divider orientation="vertical" flexItem sx={{ margin: '0 1rem 0 0' }} />
              <Link
                className={classes.link}
                to={`/development/plans/manage-plans/add/general-information`}
              >
                <Button
                  id="create-plan"
                  startIcon={<Add />}
                  color="primary"
                  variant="contained"
                  className={classes.addPlansButton}
                >
                  Add Plan
                </Button>
              </Link>
            </Box>
          }
        />
        <Grid container spacing={1} alignItems="center" className={classes.tableWrapper}>
          <Grid item xs={12}>
            <PlansFilters
              isLoading={isLoading}
              applyFilters={(clearFilters?: boolean) => {
                if (clearFilters) {
                  setFilters({
                    name: ''
                  });
                } else {
                  setPage(0);

                  setFilters({
                    ...filters,
                    name: searchValue
                  });
                }
              }}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              setSearchedValue={setSearchedValue}
              handleSearch={(val: string) => {
                setPage(0);
                setSearchedValue(val);
                setFilters({
                  ...filters,
                  name: val
                });
              }}
              hasAppliedFilters={hasAppliedFilters}
              setHasAppliedFilters={setHasAppliedFilters}
            />
            {searchedValue && (
              <SearchSection
                searchedValue={searchedValue}
                handleClearSearch={() => {
                  setSearchValue('');
                  setSearchedValue('');
                  setHasAppliedFilters(false);
                  setPage(0);
                  setFilters({
                    name: ''
                  });
                }}
              />
            )}
            <Table
              columns={columns}
              data={plans}
              isLoading={isLoading}
              hidePagination
              ResponsiveComponent={isMobile ? MobileTable : undefined}
              mobileProps={{
                fields: columns
                  .filter(col => col.Header)
                  .map(col => ({
                    name: col.Header,
                    accessor: col.accessor,
                    Cell: col?.Cell
                  })),
                handleEdit: (selectedPlan: IPlan) => {
                  handleEditPlan(selectedPlan.planId);
                }
              }}
            />
            {!isLoading && (
              <Pagination
                page={page}
                count={recordCount}
                rowsPerPage={perPage}
                setPage={setPage}
                setRowsPerPage={setRowsPerPage}
              />
            )}
          </Grid>
        </Grid>
      </Page>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  tableWrapper: {
    '@media (min-width: 960px)': {
      '& .framers-table': {
        '& .MuiTableCell-root:first-child': {
          minWidth: '200px'
        },
        '& .MuiTableCell-root': {
          maxWidth: '275px'
        }
      }
    }
  },
  actionButtons: {
    textAlign: 'right',
    display: 'flex'
  },
  link: {
    textDecoration: 'none'
  },
  addPlansButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  }
}));
